import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { FieldTypes } from '@/components/PersonInPlaceEditor/types';
import emptySelectOption from '@/constants/emptySelectOption';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import { RemitFormPayload, RemitFormValues, Props } from '@/forms/RemitForm/types';
import useInsurancePlans from '@/hooks/useInsurancePlans';
import useRemits from '@/hooks/useRemits';
import RemitRepository from '@/repositories/RemitRepository';
import { Remit } from '@/types/billing';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import InputField from '@/ui/components/Form/InputField';
import InputGroup from '@/ui/components/Form/InputGroup';
import Select from '@/ui/components/Form/Select';
import { OptionType } from '@/ui/components/Form/Select/types';
import Spinner from '@/ui/components/Spinner';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const lookupOption = (
  options: OptionType<string>[],
  value: string | null | undefined
): OptionType<string> => {
  return options.find((option: OptionType) => option.value === value) || emptySelectOption;
};

const RemitForm = ({ remit: initialRemit, onSuccess }: Props) => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const remitRepository = RemitRepository();

  const { data: insurancePlansData, isPending: isLoadingInsurancePlans } = useInsurancePlans();

  const formattedInsurancePlans = useMemo(() => {
    if (insurancePlansData?.data)
      return insurancePlansData.data.map(({ name_and_id, id }) => ({
        label: name_and_id,
        value: id
      }));
    return [];
  }, [insurancePlansData]);

  // Load options for a new remit form
  const { data: newRemitOptions, isPending: isLoadingRemitOptions } = useRemits();
  const newRemitTypes: OptionType<string>[] = useMemo(() => {
    return newRemitOptions?.data?.type || [];
  }, [newRemitOptions]);

  const newRemitPaymentMethods: OptionType<string>[] = useMemo(() => {
    return newRemitOptions?.data?.payment_method || [];
  }, [newRemitOptions]);

  // Boot data for the form

  const initialValues: RemitFormValues = useMemo(() => {
    const remit = initialRemit || {
      type: 'paper',
      ingested_at: '',
      first_financial_info: {
        check_amount: '',
        check_or_eft_trace_number: '',
        paid_at: new Date().toISOString().split('T')[0],
        payment_method: 'check',
        payer_identifier: undefined
      }
    };

    return {
      ...remit,
      type: lookupOption(newRemitTypes, remit?.type),
      payment_method: lookupOption(
        newRemitPaymentMethods,
        remit?.first_financial_info?.payment_method
      )
    };
  }, [initialRemit, newRemitTypes, newRemitPaymentMethods]);

  const {
    formState: { errors },
    control,
    handleSubmit,
    // setError,
    setValue
  } = useForm({ defaultValues: initialValues });

  useEffect(() => {
    setValue('type', lookupOption(newRemitTypes, initialRemit?.type));
    setValue(
      'payment_method',
      lookupOption(newRemitPaymentMethods, initialRemit?.first_financial_info?.payment_method)
    );
  }, [newRemitTypes, newRemitPaymentMethods, initialRemit, setValue]);

  const updateOrCreateRemit = useMutation({
    mutationFn: ({ values }: { values: RemitFormPayload }) => {
      const id = initialRemit?.id;
      if (id) {
        return remitRepository.updateRemit({ remitId: id, params: values });
      } else {
        return remitRepository.createRemit({
          params: values
        });
      }
    },
    onSuccess: (data) => {
      const remit = data.data as Remit;
      addMessage({ level: 'success', text: 'Successfully updated remit' });
      queryClient.invalidateQueries({ queryKey: ['getRemit', remit?.id] });
      // Load the updated remit in cache
      queryClient.setQueryData(['getRemit', remit?.id], remit);
      onSuccess && onSuccess(remit);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: RemitFormValues) => {
    const payload: RemitFormPayload = {
      type: values.type.value,
      ingested_at: values.ingested_at || new Date().toISOString(),
      first_financial_info: {
        payer_identifier: lookupOption(formattedInsurancePlans, values.payer_identifier?.value)
          ?.value,
        check_or_eft_trace_number: values.check_or_eft_trace_number,
        check_amount: values.check_amount,
        payment_method: values.payment_method?.value,
        paid_at: values.paid_at
      }
    };

    updateOrCreateRemit.mutate({ values: payload });
  };

  return (
    <form onSubmit={handleSubmit(submitForm)} data-testid="form-update-remit">
      <InputGroup>
        <Controller
          name="type"
          control={control}
          render={({ field }) => {
            return isLoadingRemitOptions ? (
              <Spinner />
            ) : (
              <Select
                label="Remit Type"
                onChange={field.onChange}
                options={newRemitTypes}
                placeholder="Select Remit Type"
                selectedOption={lookupOption(newRemitTypes, field.value.value)}
              />
            );
          }}
          rules={{
            validate: {
              required: (value) => !!value.value || 'Remit Type is required'
            }
          }}
        />
        <FieldError message={errors.type?.message?.toString()} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="check_or_eft_trace_number"
          control={control}
          render={({ field }) => (
            <InputField
              id={'check_or_eft_trace_number'}
              {...field}
              placeholder="X123"
              label={'Check Number'}
              type="text"
              value={field.value ?? ''}
            />
          )}
          rules={{ required: 'Check Number is required' }}
        />
        <FieldError message={errors.check_or_eft_trace_number?.message?.toString()} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="paid_at"
          control={control}
          render={({ field }) => (
            <InputField
              id={'paid_at'}
              {...field}
              placeholder="06 / 01 / 1988"
              type="date"
              label={'Paid On'}
              value={field.value ?? ''}
            />
          )}
          rules={{
            required: true,
            pattern: { value: /\d{4}-\d{2}-\d{2}/, message: 'Invalid date' }
          }}
        />
        <FieldError message={errors.paid_at?.message?.toString()} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="check_amount"
          control={control}
          render={({ field }) => (
            <InputField
              id={'check_amount'}
              {...field}
              type={FieldTypes.NUMBER}
              step={0.01}
              label={'Check Amount'}
              placeholder="100.00"
              typeHelper="dollars"
              value={field.value ?? ''}
            />
          )}
          rules={{
            required: true,
            pattern: { value: /^-?\d+(\.\d{1,2})?$/, message: 'Invalid amount' }
          }}
        />
        <FieldError message={errors.check_amount?.message?.toString()} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="payer_identifier"
          control={control}
          render={({ field }) => {
            return (
              <AutocompleteSingleSelect
                label={
                  <div className="flex items-center">
                    Select Payer / Plan
                    {isLoadingInsurancePlans && <Spinner className="ml-2" size={4} />}
                  </div>
                }
                onChange={field.onChange}
                options={formattedInsurancePlans}
                placeholder="Select Payer"
                selectedOption={lookupOption(formattedInsurancePlans, field.value?.value)}
              />
            );
          }}
          rules={{ required: true }}
        />
        <FieldError message={errors.payer_identifier?.message?.toString()} />
      </InputGroup>

      <InputGroup>
        <Controller
          name="payment_method"
          control={control}
          render={({ field }) => {
            return isLoadingRemitOptions ? (
              <Spinner />
            ) : (
              <Select
                label="Payment Method"
                onChange={field.onChange}
                options={newRemitPaymentMethods}
                placeholder="Select Payment Method"
                selectedOption={lookupOption(newRemitPaymentMethods, field.value?.value)}
              />
            );
          }}
          rules={{
            validate: {
              required: (value) => !!value?.value || 'Payment Method is required'
            }
          }}
        />
        <FieldError message={errors.type?.message?.toString()} />
      </InputGroup>

      <InputGroup>
        <Button disabled={updateOrCreateRemit.isPending} type="submit">
          {updateOrCreateRemit.isPending ? <Spinner /> : initialRemit?.id ? 'Update' : 'Create'}
        </Button>
      </InputGroup>
    </form>
  );
};

export default RemitForm;
